import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { WhatsappLogo, Storefront, Truck, PlugsConnected, CheckCircle, Warning } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

function StatusChip({ configured, connected, testing }) {
  if (testing) return <span className="text-xs px-2 py-0.5 rounded-full bg-secondary animate-pulse">Testing…</span>;
  if (connected === true) return <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 flex items-center gap-1"><CheckCircle size={12} weight="fill" />Connected</span>;
  if (connected === false) return <span className="text-xs px-2 py-1 rounded-full bg-destructive/10 text-destructive flex items-center gap-1"><Warning size={12} weight="fill" />Error</span>;
  if (configured) return <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">Configured</span>;
  return <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">Not connected</span>;
}

function ProviderCard({ icon: Icon, name, tagline, testId, existing, children, onSave, onTest, onExtras, saveDisabled }) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const runTest = async () => {
    setTesting(true); setResult(null);
    try { const r = await onTest(); setResult(r); }
    catch (e) { setResult({ ok: false, error: e.response?.data?.detail || e.message }); }
    finally { setTesting(false); }
  };
  return (
    <Card className="p-6" data-testid={testId}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon size={20} weight="duotone" className="text-primary" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-lg leading-tight">{name}</h3>
            <p className="text-xs text-muted-foreground mt-1">{tagline}</p>
          </div>
        </div>
        <StatusChip configured={!!existing} connected={result?.ok} testing={testing} />
      </div>
      <div className="space-y-3">{children}</div>
      <div className="mt-4 pt-4 border-t border-border/60 flex gap-2 flex-wrap">
        <Button onClick={onSave} disabled={saveDisabled} data-testid={`${testId}-save`}>Save</Button>
        <Button variant="outline" onClick={runTest} disabled={!existing || testing} data-testid={`${testId}-test`}>Test connection</Button>
        {onExtras}
      </div>
      {result && !result.ok && (
        <div className="mt-3 text-xs text-destructive break-words" data-testid={`${testId}-error`}>
          {result.error || `HTTP ${result.status}: ${(result.error || "").slice(0, 240)}`}
        </div>
      )}
      {result?.ok && (
        <div className="mt-3 text-xs text-emerald-700 dark:text-emerald-300" data-testid={`${testId}-ok`}>Connection verified.</div>
      )}
    </Card>
  );
}

export default function Integrations() {
  const { user } = useAuth();
  const isOwner = user?.role === "organization_owner";
  const [existing, setExisting] = useState({});
  const [wa, setWa] = useState({ access_token: "", phone_number_id: "", waba_id: "", verify_token: "" });
  const [woo, setWoo] = useState({ store_url: "", consumer_key: "", consumer_secret: "" });
  const [dl, setDl] = useState({ api_token: "", client_name: "", pickup_location_name: "", mode: "production" });
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try { const r = await api.get("/integrations"); setExisting(r.data || {}); }
    catch { setExisting({}); }
  };
  useEffect(() => { load(); }, []);

  const saveWA = async () => {
    if (!wa.access_token || !wa.phone_number_id) return toast.error("Access token & Phone Number ID required");
    try { await api.post("/integrations/whatsapp", wa); toast.success("WhatsApp saved"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const testWA = async () => (await api.post("/integrations/whatsapp/test")).data;

  const saveWoo = async () => {
    if (!woo.store_url || !woo.consumer_key || !woo.consumer_secret) return toast.error("All fields required");
    try { await api.post("/integrations/woocommerce", woo); toast.success("WooCommerce saved"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const testWoo = async () => (await api.post("/integrations/woocommerce/test")).data;
  const runWooSync = async (mode) => {
    setSyncing(true);
    try {
      const r = await api.post("/integrations/woocommerce/sync", { mode });
      toast.success(`Synced ${mode}: +${r.data.imported} new, ${r.data.updated} updated`);
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const saveDl = async () => {
    if (!dl.api_token || !dl.client_name || !dl.pickup_location_name) return toast.error("All fields required");
    try { await api.post("/integrations/delhivery", dl); toast.success("Delhivery saved"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const testDl = async () => (await api.post("/integrations/delhivery/test")).data;

  if (!isOwner) {
    return (
      <div className="p-8 text-muted-foreground" data-testid="integrations-locked">
        Only the organization owner can manage third-party integrations.
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="integrations-page">
      <div>
        <div className="overline text-muted-foreground mb-2">Connections</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">Integrations</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Wire your CRM to the tools your team already uses. Credentials are stored per workspace and only visible to the organization owner.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* WhatsApp */}
        <ProviderCard
          icon={WhatsappLogo} name="WhatsApp Business" testId="int-whatsapp"
          tagline="Send messages via Meta Cloud API. Requires a permanent System User token."
          existing={existing.whatsapp} onSave={saveWA} onTest={testWA}
        >
          <div>
            <Label className="text-xs">ACCESS TOKEN</Label>
            <Input type="password" value={wa.access_token} onChange={(e) => setWa({ ...wa, access_token: e.target.value })}
              placeholder={existing.whatsapp?.access_token || "EAAB…"} data-testid="wa-token" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">PHONE NUMBER ID</Label>
              <Input value={wa.phone_number_id} onChange={(e) => setWa({ ...wa, phone_number_id: e.target.value })}
                placeholder={existing.whatsapp?.phone_number_id || ""} data-testid="wa-phone" />
            </div>
            <div><Label className="text-xs">WABA ID</Label>
              <Input value={wa.waba_id} onChange={(e) => setWa({ ...wa, waba_id: e.target.value })}
                placeholder={existing.whatsapp?.waba_id || ""} data-testid="wa-waba" />
            </div>
          </div>
          <div><Label className="text-xs">WEBHOOK VERIFY TOKEN</Label>
            <Input value={wa.verify_token} onChange={(e) => setWa({ ...wa, verify_token: e.target.value })}
              placeholder={existing.whatsapp?.verify_token || "any random string"} data-testid="wa-verify" />
            <p className="text-[11px] text-muted-foreground mt-1">Webhook URL: <code className="text-foreground">{`${process.env.REACT_APP_BACKEND_URL || ""}/api/integrations/whatsapp/webhook`}</code></p>
          </div>
        </ProviderCard>

        {/* WooCommerce */}
        <ProviderCard
          icon={Storefront} name="WooCommerce" testId="int-woocommerce"
          tagline="Sync products and orders from your Woo store into the CRM."
          existing={existing.woocommerce} onSave={saveWoo} onTest={testWoo}
          onExtras={<>
            <Button variant="outline" onClick={() => runWooSync("products")} disabled={!existing.woocommerce || syncing} data-testid="int-woocommerce-sync-products">
              {syncing ? "Syncing…" : "Sync products"}
            </Button>
            <Button variant="outline" onClick={() => runWooSync("orders")} disabled={!existing.woocommerce || syncing} data-testid="int-woocommerce-sync-orders">
              {syncing ? "Syncing…" : "Sync orders"}
            </Button>
          </>}
        >
          <div>
            <Label className="text-xs">STORE URL</Label>
            <Input value={woo.store_url} onChange={(e) => setWoo({ ...woo, store_url: e.target.value })}
              placeholder={existing.woocommerce?.store_url || "https://yourstore.com"} data-testid="woo-url" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">CONSUMER KEY</Label>
              <Input value={woo.consumer_key} onChange={(e) => setWoo({ ...woo, consumer_key: e.target.value })}
                placeholder={existing.woocommerce?.consumer_key || "ck_…"} data-testid="woo-ck" />
            </div>
            <div><Label className="text-xs">CONSUMER SECRET</Label>
              <Input type="password" value={woo.consumer_secret} onChange={(e) => setWoo({ ...woo, consumer_secret: e.target.value })}
                placeholder={existing.woocommerce?.consumer_secret || "cs_…"} data-testid="woo-cs" />
            </div>
          </div>
        </ProviderCard>

        {/* Delhivery */}
        <ProviderCard
          icon={Truck} name="Delhivery" testId="int-delhivery"
          tagline="Create shipments and track waybills for confirmed orders."
          existing={existing.delhivery} onSave={saveDl} onTest={testDl}
        >
          <div><Label className="text-xs">API TOKEN</Label>
            <Input type="password" value={dl.api_token} onChange={(e) => setDl({ ...dl, api_token: e.target.value })}
              placeholder={existing.delhivery?.api_token || ""} data-testid="dl-token" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">CLIENT NAME</Label>
              <Input value={dl.client_name} onChange={(e) => setDl({ ...dl, client_name: e.target.value })}
                placeholder={existing.delhivery?.client_name || "As per Delhivery"} data-testid="dl-client" />
            </div>
            <div><Label className="text-xs">PICKUP LOCATION</Label>
              <Input value={dl.pickup_location_name} onChange={(e) => setDl({ ...dl, pickup_location_name: e.target.value })}
                placeholder={existing.delhivery?.pickup_location_name || "Main Warehouse"} data-testid="dl-pickup" />
            </div>
          </div>
          <div><Label className="text-xs">MODE</Label>
            <Select value={dl.mode} onValueChange={(v) => setDl({ ...dl, mode: v })}>
              <SelectTrigger data-testid="dl-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="production">Production</SelectItem>
                <SelectItem value="staging">Staging</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </ProviderCard>
      </div>

      <Card className="p-5 border-primary/30 bg-primary/5">
        <div className="flex gap-3">
          <PlugsConnected size={20} weight="duotone" className="text-primary shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium">How this works</div>
            <p className="text-muted-foreground mt-1">
              Save your credentials here; the CRM will use them to call each provider on your behalf. Nothing is shared across workspaces. To actually send a WhatsApp message or create a shipment, use the action buttons on Customers and Orders.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
